// Right-edge dock modal showing the active engagement's round list and a
// tab strip of queued fights. Always mounted when the toggle is 'modal'
// or 'modal+drawer'; renders null when no fights are queued.
//
// Spec: docs/02-systems/combat-visualisation.md §1a, §4.

import { useT } from '../../i18n/index.js';
import { appDom } from '../../lib/appDom.js';
import type { CombatFightRef, CombatRound } from '../../render/combatRounds.js';
import { CombatFightHeader } from './CombatFightHeader.js';
import { CombatRoundList } from './CombatRoundList.js';

interface CombatModalProps {
  readonly fights: ReadonlyArray<CombatFightRef>;
  readonly roundsByFight: ReadonlyMap<string, ReadonlyArray<CombatRound>>;
  readonly selectedIndex: number;
  readonly attackerPlayerIndexByFight?: ReadonlyMap<string, number>;
  readonly onSelectFight: (index: number) => void;
  readonly onMinimize: () => void;
  readonly onClose: () => void;
}

function fightKey(f: CombatFightRef): string {
  return `${f.attackerId}::${f.defenderId}`;
}

export function CombatModal({
  fights,
  roundsByFight,
  selectedIndex,
  attackerPlayerIndexByFight,
  onSelectFight,
  onMinimize,
  onClose,
}: CombatModalProps) {
  const t = useT();
  if (fights.length === 0) return null;
  const selected = fights[selectedIndex] ?? fights[0];
  if (!selected) return null;
  const rounds = roundsByFight.get(fightKey(selected)) ?? [];

  return (
    <aside
      aria-label={t('combat.engagement')}
      data-testid="combat-modal"
      {...appDom({
        id: 'combat-modal',
        type: 'Drawer',
        scope: 'drawer',
        label: t('combat.engagement'),
      })}
      className="fixed top-(--hud-panel-top) right-3 md:right-(--centre-inset) z-50 flex h-[460px] w-[340px] flex-col gap-3 rounded-xl border border-rose-500/30 bg-card/95 p-3 text-foreground shadow-2xl backdrop-blur-sm"
    >
      <CombatFightHeader
        fights={fights}
        selectedIndex={Math.min(selectedIndex, fights.length - 1)}
        attackerPlayerIndexByFight={attackerPlayerIndexByFight}
        onSelectFight={onSelectFight}
      />

      <div className="flex-1 overflow-y-auto">
        <CombatRoundList rounds={rounds} />
      </div>

      <footer className="flex justify-between gap-2 border-t border-border/60 pt-2">
        <button
          type="button"
          onClick={onMinimize}
          className="rounded-md border border-border/60 bg-background/40 px-3 py-1 text-xs uppercase tracking-wider text-muted-foreground transition hover:bg-background/70 hover:text-foreground"
        >
          {t('combat.minimize')}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-1 text-xs uppercase tracking-wider text-rose-800 transition hover:bg-rose-500/20 dark:text-rose-200"
        >
          {t('common.close')}
        </button>
      </footer>
    </aside>
  );
}
